import { API_ENDPOINTS, APP_CONFIG } from "@/config/app";
import { resolvePortalType } from "@/services/auth/resolvePortalType";
import { getStoredTokens } from "@/services/auth/tokenStorage";
import { AuthRole } from "@/store/AuthStore";
import { rootStore } from "@/store/RootStore";
import type { AuthTokens } from "@/types/api";

const AUTH_REFRESH_ENDPOINTS = {
  ADMIN: `${API_ENDPOINTS.ADMIN}/auth/refresh`,
  RESTAURANT: `${API_ENDPOINTS.RESTAURANTS}/auth/refresh`,
} as const;

const REFRESH_ENDPOINTS = new Set<string>(
  Object.values(AUTH_REFRESH_ENDPOINTS)
);

export function isRefreshEndpoint(endpoint: string): boolean {
  return REFRESH_ENDPOINTS.has(endpoint);
}

let refreshPromise: Promise<void> | null = null;

async function requestTokenRefresh(): Promise<AuthTokens> {
  const storedTokens = getStoredTokens();

  if (!storedTokens?.refreshToken) {
    throw new Error("Missing refresh token");
  }

  const portalType = resolvePortalType();
  const endpoint =
    portalType === AuthRole.ADMIN
      ? AUTH_REFRESH_ENDPOINTS.ADMIN
      : AUTH_REFRESH_ENDPOINTS.RESTAURANT;

  const response = await fetch(`${APP_CONFIG.apiUrl}${endpoint}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      refreshToken: storedTokens.refreshToken,
    }),
  });

  if (!response.ok) {
    throw new Error("Token refresh failed");
  }

  return response.json() as Promise<AuthTokens>;
}

export function refreshTokens(): Promise<void> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const tokens = await requestTokenRefresh();
        rootStore.authStore.setTokens(tokens.token, tokens.refreshToken);
      } catch {
        rootStore.authStore.logout();
        throw new Error("Token refresh failed");
      }
    })().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
}
